import { NavLink, useLocation, useParams } from "react-router";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubItem,
} from "./ui/sidebar";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronRight } from "@/assets";
import { useDeviceGroupDetail } from "@/api/device-group-http-functions";
import { useSiteCoreDevice } from "@/hooks/useSiteCoreDevice";
import { DeviceDto, IlluminateUserPermissionsEnum } from "@/api/openapi";
import { DeviceDtoDeviceType } from "@/api/query.schemas";
import { useDeviceListODataServer } from "@/api/odata/odata-hooks.gen";
import { FeatureFlag } from "@/constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isLoadManagementQualifyingCore } from "@/utils/isLoadManagementQualifyingCore";
import { isCoreChargerLogsDevice } from "@/utils/isCoreChargerLogsDevice";
import AccessGuard from "@/components/access-guard";
import { Skeleton } from "./ui/skeleton";
import { BodyLabelSmall } from "@/components/ui/typography";
import { useMemo, useState } from "react";
import { LoadManagementLinks } from "@/components/load-management-links";

/** How many device links to load per page in the sidebar. */
const DEVICE_PAGE_SIZE = 20;

/**
 * The "Devices" nav item with an expandable, nested list of the device group's
 * devices (core devices first). The first {@link DEVICE_PAGE_SIZE} devices are
 * loaded eagerly so the chevron can be hidden when the group has no devices,
 * and a count is shown next to the link. Used only for non-organisation groups.
 */
const DeviceGroupDevicesNav = ({
    deviceGroupTag,
    base,
    defaultOpen,
}: {
    deviceGroupTag: string;
    base: string;
    defaultOpen: boolean;
}) => {
    const [visibleCount, setVisibleCount] = useState(DEVICE_PAGE_SIZE);

    const {
        data: devices,
        rowCount,
        isLoading,
    } = useDeviceListODataServer(
        {
            filters: [
                {
                    field: "deviceGroupTags",
                    op: "anyEq",
                    value: deviceGroupTag,
                },
            ],
            orderby: [{ field: "friendlyName" }],
            top: visibleCount,
        },
        { enabled: !!deviceGroupTag },
    );

    // Stable sort so core devices are shown first within the loaded set.
    const sortedDevices = useMemo(
        () =>
            devices.toSorted(
                (a, b) =>
                    Number(b.deviceType === DeviceDtoDeviceType.Core) -
                    Number(a.deviceType === DeviceDtoDeviceType.Core),
            ),
        [devices],
    );

    const devicesLink = (
        <SidebarMenuButton asChild>
            <NavLink className={"text-default-color"} to={base} end>
                <span>Devices</span>
                {rowCount > 0 && (
                    <BodyLabelSmall as="span" className="ml-auto text-inherit">
                        {rowCount}
                    </BodyLabelSmall>
                )}
            </NavLink>
        </SidebarMenuButton>
    );

    // No devices (or still loading the first page): plain link, no chevron.
    if (isLoading || rowCount === 0) {
        return <SidebarMenuItem>{devicesLink}</SidebarMenuItem>;
    }

    return (
        <Collapsible
            className="group/devices-collapsible"
            defaultOpen={defaultOpen}
        >
            <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        aria-label="Toggle devices"
                        className="absolute -left-6 flex h-10 items-center"
                    >
                        <ChevronRight className="transition-transform group-data-[state=open]/devices-collapsible:rotate-90" />
                    </button>
                </CollapsibleTrigger>
                {devicesLink}
                <CollapsibleContent>
                    <SidebarMenuSub>
                        {sortedDevices.map((device) => (
                            <SidebarMenuSubItem key={device.deviceIdentity}>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`${base}/${device.deviceIdentity}`}
                                        end
                                    >
                                        <span>
                                            {device.friendlyName ??
                                                device.deviceIdentity}
                                        </span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuSubItem>
                        ))}
                        {rowCount > sortedDevices.length && (
                            <SidebarMenuSubItem>
                                <SidebarMenuButton
                                    onClick={() =>
                                        setVisibleCount(
                                            (count) => count + DEVICE_PAGE_SIZE,
                                        )
                                    }
                                >
                                    <span>Show more</span>
                                </SidebarMenuButton>
                            </SidebarMenuSubItem>
                        )}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
};

/** A single core's nested group of Load Management links, used when a site has more than one qualifying core. */
const CoreLoadManagementGroup = ({
    core,
    loadManagementBase,
    defaultOpen,
}: {
    core: DeviceDto;
    loadManagementBase: string;
    defaultOpen: boolean;
}) => (
    <Collapsible className="group/core-collapsible" defaultOpen={defaultOpen}>
        <SidebarMenuItem>
            <CollapsibleTrigger className="relative w-full">
                <div className="absolute -left-6 flex h-10 items-center">
                    <ChevronRight className="transition-transform group-data-[state=open]/core-collapsible:rotate-90" />
                </div>
                <SidebarMenuButton asChild>
                    <span>{core.friendlyName ?? core.deviceIdentity}</span>
                </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <SidebarMenuSub>
                    <LoadManagementLinks
                        base={`${loadManagementBase}/${core.deviceIdentity}`}
                        isCoreChargerLogs={isCoreChargerLogsDevice(core)}
                    />
                </SidebarMenuSub>
            </CollapsibleContent>
        </SidebarMenuItem>
    </Collapsible>
);

export const DeviceGroupSidebar = ({
    ...props
}: React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
}) => {
    const { deviceGroupTag } = useParams<{ deviceGroupTag: string }>();
    const { data: deviceGroup, isLoading } = useDeviceGroupDetail(
        deviceGroupTag!,
    );
    const location = useLocation();

    const isSite = deviceGroup?.deviceGroupType === "Site";
    const isOrganisation = deviceGroup?.deviceGroupType === "Organisation";
    const isLoadManagementEnabled =
        useFlags()[FeatureFlag.IL_4187_LOAD_MANAGEMENT];

    const { cores } = useSiteCoreDevice(deviceGroupTag, {
        enabled: isSite && isLoadManagementEnabled,
    });

    const qualifyingCores = cores.filter(isLoadManagementQualifyingCore);
    const loadManagementEnabled = qualifyingCores.length > 0;

    const deviceGroupPagePath = useMemo(() => {
        const segments = location.pathname.split("/");
        const tagIndex = segments.findIndex(
            (segment) => decodeURIComponent(segment) === deviceGroupTag,
        );
        if (tagIndex !== -1) {
            return segments.slice(0, tagIndex + 1).join("/");
        }
        // Fallback if the tag isn't in the path yet (e.g. mid-navigation).
        return `/manage-customers/device-groups/${deviceGroup?.deviceGroupTag}`;
    }, [deviceGroup?.deviceGroupTag, deviceGroupTag, location.pathname]);

    const loadManagementBase = `${deviceGroupPagePath}/devices`;

    return (
        <Sidebar {...props}>
            {props.children}
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>
                        {isLoading ? (
                            <Skeleton className="h-8 w-50" />
                        ) : (
                            deviceGroup?.deviceGroupName
                        )}
                    </SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`${deviceGroupPagePath}/overview`}
                                    >
                                        <span>Overview</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            {isOrganisation ? (
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`${deviceGroupPagePath}/devices`}
                                            end
                                        >
                                            <span>Devices</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ) : (
                                <DeviceGroupDevicesNav
                                    deviceGroupTag={deviceGroupTag!}
                                    base={`${deviceGroupPagePath}/devices`}
                                    defaultOpen={location.pathname.includes(
                                        `${deviceGroupPagePath}/devices/`,
                                    )}
                                />
                            )}
                            {isSite && (
                                <AccessGuard
                                    unauthorizedBehaviour="hide"
                                    featureFlags={[
                                        FeatureFlag.IL_4187_LOAD_MANAGEMENT,
                                    ]}
                                    permissions={[
                                        IlluminateUserPermissionsEnum.SystemDeviceProvisioning,
                                        IlluminateUserPermissionsEnum.PartnerDeviceProvisioning,
                                    ]}
                                    permissionsSelector="any"
                                    overrideAndRestrict={!loadManagementEnabled}
                                >
                                    <Collapsible
                                        className="group/collapsible"
                                        defaultOpen={location.pathname.includes(
                                            loadManagementBase,
                                        )}
                                    >
                                        <SidebarMenuItem>
                                            <CollapsibleTrigger className="relative w-full">
                                                <div className="absolute -left-6 flex h-10 items-center">
                                                    <ChevronRight className="transition-transform group-data-[state=open]/collapsible:rotate-90" />
                                                </div>
                                                <SidebarMenuButton asChild>
                                                    <span>Load management</span>
                                                </SidebarMenuButton>
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                                <SidebarMenuSub>
                                                    {qualifyingCores.map(
                                                        (core) => (
                                                            <CoreLoadManagementGroup
                                                                key={
                                                                    core.deviceIdentity
                                                                }
                                                                core={core}
                                                                loadManagementBase={
                                                                    loadManagementBase
                                                                }
                                                                defaultOpen={
                                                                    qualifyingCores.length ===
                                                                        1 ||
                                                                    location.pathname.includes(
                                                                        `${loadManagementBase}/${core.deviceIdentity}`,
                                                                    )
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </SidebarMenuSub>
                                            </CollapsibleContent>
                                        </SidebarMenuItem>
                                    </Collapsible>
                                </AccessGuard>
                            )}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
        </Sidebar>
    );
};
